import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileStack, CheckCircle2, XCircle,
  Loader2, ClipboardList, Inbox,
} from 'lucide-react';
import { KPIStatCard } from './KPIStatCard';
import { RecentJobsTable } from './RecentJobsTable';
import { ContractsOverviewWidget } from './ContractsOverviewWidget';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useJobList } from '@/hooks/useJobList';
import { api } from '@/lib/api';
import { FEATURE_ESCALATIONS } from '@/lib/features';
import type { JobStatus, FinalDecision, ReviewStatus } from '@/types';

// ─── Types ──────────────────────────────────────────────────────────────────

interface OverviewTabProps {
  /** Called when user clicks the Awaiting Review KPI card */
  onGoToNeedsReview: () => void;
}

type SortKey = 'file_name' | 'status' | 'final_decision' | 'created_at';

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Overview tab: 5 KPI stat cards + filterable, sortable job history table.
 * Each section loads independently (progressive loading pattern).
 */
export function OverviewTab({ onGoToNeedsReview }: OverviewTabProps) {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<JobStatus | ''>('');
  const [decisionFilter, setDecisionFilter] = useState<FinalDecision | ''>('');
  const [reviewFilter, setReviewFilter] = useState<ReviewStatus | ''>('');
  const [page, setPage] = useState(1);
  const [openEscalations, setOpenEscalations] = useState<number | null>(null);

  useEffect(() => {
    if (!FEATURE_ESCALATIONS) {
      setOpenEscalations(null);
      return;
    }
    api
      .get<{ data: { id: string }[] }>('/api/v1/escalations?status=open')
      .then((r) => { setOpenEscalations(r.data.length); })
      .catch(() => { setOpenEscalations(null); });
  }, []);

  function applyFilters(next: { status?: JobStatus | ''; decision?: FinalDecision | ''; review?: ReviewStatus | '' }) {
    setStatusFilter(next.status ?? '');
    setDecisionFilter(next.decision ?? '');
    setReviewFilter(next.review ?? '');
    setPage(1);
  }
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data: jobs, isLoading: jobsLoading, error: jobsError, refetch } = useJobList({
    status: statusFilter || undefined,
    decision: decisionFilter || undefined,
    review_status: reviewFilter || undefined,
    page,
    limit: 10,
  });

  const hasFilters = Boolean(statusFilter || decisionFilter || reviewFilter);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(1);
  }

  function clearFilters() {
    setStatusFilter('');
    setDecisionFilter('');
    setReviewFilter('');
    setPage(1);
  }

  return (
    <div className="space-y-6">
      {/* KPI row — every card is interactive. Most apply filters to the
          table below; "Awaiting Review" jumps to the Needs Review tab;
          "My Escalations" deep-links to /escalations. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <KPIStatCard
          title="Total Jobs"
          value={stats?.total_jobs ?? 0}
          icon={FileStack}
          loading={statsLoading}
          onClick={() => { applyFilters({}); }}
        />
        <KPIStatCard
          title="Approved"
          value={stats?.approved ?? 0}
          icon={CheckCircle2}
          variant="success"
          loading={statsLoading}
          onClick={() => { applyFilters({ status: 'completed', decision: 'APPROVED' }); }}
        />
        <KPIStatCard
          title="Rejected"
          value={stats?.rejected ?? 0}
          icon={XCircle}
          variant="danger"
          loading={statsLoading}
          onClick={() => { applyFilters({ decision: 'REJECTED' }); }}
        />
        <KPIStatCard
          title="In Progress"
          value={stats?.in_progress ?? 0}
          icon={Loader2}
          loading={statsLoading}
          onClick={() => { applyFilters({ status: 'processing' }); }}
        />
        <KPIStatCard
          title="Awaiting Review"
          value={stats?.awaiting_review ?? 0}
          icon={ClipboardList}
          variant="warning"
          onClick={onGoToNeedsReview}
          loading={statsLoading}
        />
        {FEATURE_ESCALATIONS && (
          <KPIStatCard
            title="My Escalations"
            value={openEscalations ?? 0}
            icon={Inbox}
            variant={openEscalations && openEscalations > 0 ? 'warning' : 'default'}
            onClick={() => { navigate('/escalations'); }}
            loading={openEscalations === null}
          />
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as JobStatus | ''); setPage(1); }}
          className="rounded border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          aria-label="Filter by AI decision"
          value={decisionFilter}
          onChange={(e) => { setDecisionFilter(e.target.value as FinalDecision | ''); setPage(1); }}
          className="rounded border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">All Decisions</option>
          <option value="APPROVED">Approved</option>
          <option value="APPROVED_WITH_ALERTS">Approved with Alerts</option>
          <option value="REJECTED">Rejected</option>
          <option value="ESCALATED">Escalated</option>
        </select>

        <select
          aria-label="Filter by review status"
          value={reviewFilter}
          onChange={(e) => { setReviewFilter(e.target.value as ReviewStatus | ''); setPage(1); }}
          className="rounded border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">All Review Statuses</option>
          <optgroup label="Final review">
            <option value="pending_review">Pending Review</option>
            <option value="claimed">Claimed</option>
            <option value="in_progress">In Progress</option>
            <option value="reviewed">Reviewed</option>
            <option value="escalated">Escalated</option>
            <option value="returned">Returned</option>
          </optgroup>
          <optgroup label="Paused at stage">
            <option value="paused_at_document_assistant">Paused — Document Assistant (OCR)</option>
            <option value="paused_at_document">Paused — Document</option>
            <option value="paused_at_parties">Paused — Parties</option>
            <option value="paused_at_clause">Paused — Clause</option>
            <option value="paused_at_gate_1">Paused — Gate 1</option>
            <option value="paused_at_compliance">Paused — Compliance</option>
            <option value="paused_at_signature">Paused — Signature</option>
            <option value="paused_at_gate_2">Paused — Gate 2</option>
            <option value="paused_at_gate">Paused — Final Gate</option>
            <option value="paused_at_orchestrator_bootstrap">Paused — Orchestrator</option>
          </optgroup>
        </select>

        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="rounded px-3 py-1.5 text-sm text-muted-foreground underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Contracts × stages overview (auto-refresh every 5 min) */}
      <ContractsOverviewWidget />

      {/* Job history table */}
      <RecentJobsTable
        items={jobs?.items ?? []}
        total={jobs?.total ?? 0}
        page={page}
        onPageChange={setPage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        loading={jobsLoading}
        error={jobsError}
        onRetry={() => void refetch()}
      />
    </div>
  );
}
